import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useRef, } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn.js';
const DialogContext = createContext(null);
function useDialogContext() {
    const ctx = useContext(DialogContext);
    if (ctx === null) {
        throw new Error('Dialog components must be rendered inside <Dialog>.');
    }
    return ctx;
}
export function Dialog({ open, onOpenChange, children }) {
    const baseId = useId();
    return (_jsx(DialogContext.Provider, { value: {
            open,
            onOpenChange,
            titleId: `${baseId}-title`,
            descriptionId: `${baseId}-description`,
        }, children: children }));
}
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
function focusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null || el === document.activeElement);
}
export function DialogContent({ children, className, showCloseButton = true, onOpenAutoFocus, closeLabel = 'Close', }) {
    const { open, onOpenChange, titleId, descriptionId } = useDialogContext();
    const panelRef = useRef(null);
    const restoreFocusRef = useRef(null);
    const close = useCallback(() => {
        onOpenChange(false);
    }, [onOpenChange]);
    useEffect(() => {
        if (!open)
            return;
        const panel = panelRef.current;
        if (panel === null)
            return;
        restoreFocusRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        // Lock the page behind the dialog — the backdrop covers the viewport but
        // wheel/trackpad gestures still scroll it unless the body is frozen. The
        // scrollbar-width padding keeps the header from shifting when the
        // scrollbar disappears.
        const { overflow, paddingRight } = document.body.style;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${String(scrollbarWidth)}px`;
        }
        const autoFocus = { prevented: false };
        onOpenAutoFocus?.({
            preventDefault: () => {
                autoFocus.prevented = true;
            },
        });
        if (!autoFocus.prevented) {
            const first = focusableElements(panel)[0];
            (first ?? panel).focus();
        }
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                close();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const items = focusableElements(panel);
            if (items.length === 0) {
                event.preventDefault();
                panel.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            if (first === undefined || last === undefined)
                return;
            const active = document.activeElement;
            if (event.shiftKey && (active === first || !panel.contains(active))) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && (active === last || !panel.contains(active))) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.body.style.overflow = overflow;
            document.body.style.paddingRight = paddingRight;
            restoreFocusRef.current?.focus();
            restoreFocusRef.current = null;
        };
    }, [open, close, onOpenAutoFocus]);
    if (!open)
        return null;
    return createPortal(_jsxs("div", { className: "fixed inset-0 z-modal flex items-center justify-center", role: "presentation", children: [_jsx("div", { className: "absolute inset-0 bg-black/40", "aria-hidden": "true", onClick: close, "data-testid": "dialog-overlay" }), _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": descriptionId, tabIndex: -1, className: cn('relative z-10 w-[calc(100%-2rem)] max-w-md rounded-[var(--radius-card)] border border-border bg-surface p-6 shadow-overlay', className), children: [showCloseButton && (_jsx("button", { type: "button", onClick: close, "aria-label": closeLabel, className: "absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) })), children] })] }), document.body);
}
export function DialogHeader({ className, children, }) {
    return _jsx("div", { className: cn('space-y-1.5 pr-8', className), children: children });
}
export function DialogTitle({ className, children, }) {
    const { titleId } = useDialogContext();
    return (_jsx("h2", { id: titleId, className: cn('text-lg font-bold text-foreground', className), children: children }));
}
export function DialogDescription({ className, children, }) {
    const { descriptionId } = useDialogContext();
    return (_jsx("p", { id: descriptionId, className: cn('text-sm text-muted-foreground', className), children: children }));
}
export function DialogFooter({ className, children, }) {
    return (_jsx("div", { className: cn('flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end', className), children: children }));
}
export function DialogClose({ children, className }) {
    const { onOpenChange } = useDialogContext();
    return (_jsx("button", { type: "button", className: className, onClick: () => {
            onOpenChange(false);
        }, children: children }));
}
//# sourceMappingURL=dialog.js.map